/**
 * Adapter layer between `apps/ordo`'s REST shape
 * (`SubstitutionRequestSummary` from `@/api/substitution-requests`) and
 * the chefs-v2 UI domain type (`Substitution` from `@/lib/chefs/substitutions`).
 *
 * The UI components were authored against a mock data layer with
 * lowercase status strings and a `scenarioId` field. Hunter's backend
 * uses uppercase statuses and `vendorLocationId`. Rather than churning
 * the UI on day-one, we map at the boundary.
 */
import type { SubstitutionRequestSummary } from '@/api/substitution-requests'
import { ingredientKeyFor, type Substitution } from '@/lib/chefs/substitutions'

const STATUS_MAP: Record<SubstitutionRequestSummary['status'], Substitution['status']> = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
}

function toIsoDate(value: string): string {
  // Server returns either YYYY-MM-DD or a full ISO timestamp; normalize to date.
  return value.slice(0, 10)
}

/**
 * Map a server-side request row into the UI's `Substitution` shape.
 * `scenarioId` is set to `vendorLocationId` so existing client-side
 * `subs.filter(s => s.scenarioId === currentScenario)` patterns keep
 * working unchanged when the "scenario" is just the active kitchen.
 */
export function toUiSubstitution(row: SubstitutionRequestSummary): Substitution {
  return {
    id: row.id,
    scenarioId: row.vendorLocationId,
    ingredientKey: ingredientKeyFor(row.ingredientName, row.ingredientUnit),
    ingredientName: row.ingredientName,
    ingredientUnit: row.ingredientUnit,
    replacement: row.replacement,
    reason: row.reason,
    startDate: toIsoDate(row.startDate),
    endDate: toIsoDate(row.endDate),
    status: STATUS_MAP[row.status],
    rejectionReason: row.rejectionReason ?? undefined,
    submittedAt: row.submittedAt,
    resolvedAt: row.resolvedAt ?? undefined,
  }
}

export function toUiSubstitutions(rows: SubstitutionRequestSummary[]): Substitution[] {
  return rows.map(toUiSubstitution)
}
